import { css } from '@emotion/react';
import { Dialog, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { Dispatch, SetStateAction, useState } from 'react';
import { formatCurrency } from 'shared';

import { LinkAsButton } from '../atoms';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-4']};
  `,
  priceDetail: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    color: ${semanticTokens.color.text.primary};
    display: flex;
    justify-content: space-between;
  `,
  breakdownContainer: css`
    dl {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: ${tokens.dimension['space-6']};
    }
  `,
  lineItem: (total?: boolean) => css`
    display: flex;
    justify-content: space-between;
    & > dt,
    & > dd {
      font-size: ${total ? tokens.fontSize.heading['title-2'] : tokens.fontSize.paragraph['paragraph-2']};
      color: ${semanticTokens.color.text.primary};
      ${total && `font-weight: 700;`}
    }
  `,
};

export type BreakdownRow = {
  label: string;
  price: number;
};

type PriceDetail = {
  label: string;
  price: string;
  breakdown?: BreakdownRow[];
};

type Props = {
  priceDetails: PriceDetail[];
  handleOpen?: Dispatch<SetStateAction<boolean>>;
  pos: string;
};

export function PriceDetails({ priceDetails, pos }: Props) {
  const [selectedBreakdown, selectBreakdown] = useState<{ title: string; rows: BreakdownRow[] } | undefined>(undefined);
  return (
    <>
      <dl css={styles.container}>
        {priceDetails.map(({ label, price, breakdown }) => {
          const hasBreakdown = !!breakdown?.length;

          return (
            <div key={label} css={styles.priceDetail}>
              {hasBreakdown ? (
                <LinkAsButton onClick={() => selectBreakdown({ title: label, rows: breakdown })}>{label}</LinkAsButton>
              ) : (
                <dt>{label}</dt>
              )}
              <dd>{price}</dd>
            </div>
          );
        })}
      </dl>
      {selectedBreakdown && (
        <Dialog.Root>
          <Dialog.Window
            onCloseLabel="Close"
            closeOnEscape
            onClose={() => selectBreakdown(undefined)}
            title={selectedBreakdown.title}
          >
            <Dialog.Container>
              <Breakdown rows={selectedBreakdown.rows} pos={pos} />
            </Dialog.Container>
          </Dialog.Window>
        </Dialog.Root>
      )}
    </>
  );
}

type BreakdownProps = {
  rows: BreakdownRow[];
  pos: string;
};

const Breakdown = ({ rows, pos }: BreakdownProps) => {
  const total = rows.reduce((acc, { price }) => acc + price, 0);

  const LineItem = ({ label, price, total }: { label: string; price: number; total?: boolean }) => (
    <div key={label} css={styles.lineItem(total)}>
      <dt dangerouslySetInnerHTML={{ __html: label }}></dt>
      <dd>{formatCurrency(price, pos, { displayLocalFormats: true })}</dd>
    </div>
  );

  return (
    <div css={styles.breakdownContainer}>
      <dl>
        {rows.map(({ label, price }) => (
          <LineItem key={label} label={label} price={price} />
        ))}
        <LineItem total label={t('common:total')} price={total} />
      </dl>
    </div>
  );
};
